/**
 * Launches API Client
 *
 * Handles all API calls related to NFT launches (creator drops via Launchpad feature)
 */

import { apiClient, ApiResponse } from './client';
import type { XamanPayloadResponse } from '../types/signing';

/** Backend SigningIntent shape (provider-dispatched signing). Present alongside `payload`
 *  for Xaman, and the only signing ref for client-signing wallets (joey-wc). */
type SigningIntentResponse = { intentId: string; provider: string; txJson: Record<string, unknown>; xaman?: unknown };

// ==================== Types ====================

export type LaunchStatus =
  | 'draft'
  | 'pending_approval'
  | 'pending_authorization'
  | 'pending_payment'
  | 'processing'
  | 'ready'
  | 'active'
  | 'paused'
  | 'completed'
  | 'cancelled';

export type MintPhase = 'allowlist' | 'allowlist_2' | 'public';

export interface ItemCounts {
  pending: number;
  reserved: number;
  minting: number;
  minted: number;
  failed: number;
}

export type RequirementType = 'holds_collection' | 'minted_on_deals' | 'minted_from_collection' | 'offers_copied' | 'twitter_follow' | 'paid_registration';

export interface RegistrationRequirement {
  type: RequirementType;
  collectionIds?: string[];
  minCount?: number;
  twitterHandle?: string;
  priceDrops?: number;
  maxQuantity?: number | null;
}

export interface Launch {
  id: string;
  project_id: string | null;
  collection_id: string | null;
  creator_address: string;
  creator_user_id: string | null;
  name: string;
  slug: string;
  description: string | null;
  taxon: number;
  thumbnail_url: string | null;
  banner_url: string | null;
  banner_position_y: number;
  website_url: string | null;
  twitter_handle: string | null;
  discord_url: string | null;
  total_supply: number | null;
  minted_count: number;
  transfer_fee: number;
  nft_flags: number;
  mint_price_drops: number | null; // Public phase price
  allowlist_mint_price_drops: number | null; // Allowlist phase 1 price
  allowlist_2_mint_price_drops: number | null; // Allowlist phase 2 price
  limit_per_wallet: number | null; // Public phase limit
  mint_phase: MintPhase | null; // Current phase when active
  allowlist_price_locked_at: string | null;
  allowlist_2_price_locked_at: string | null;
  public_price_locked_at: string | null;
  allowlist_start_at: string | null;
  allowlist_2_start_at: string | null;
  public_start_at: string | null;
  end_at: string | null;
  minter_authorized: boolean;
  authorization_tx_hash: string | null;
  authorized_at: string | null;
  fee_paid: boolean;
  fee_payment_tx_hash: string | null;
  fee_destination_tag: number | null;
  fee_amount_drops: number | null;
  fee_paid_at: string | null;
  media_uploaded: boolean;
  ipfs_uploaded: boolean;
  media_checks_complete: boolean;
  admin_approved: boolean;
  admin_approved_by: string | null;
  admin_approved_at: string | null;
  admin_rejection_reason: string | null;
  status: LaunchStatus;
  allowlist_enabled: boolean;
  allowlist_2_enabled: boolean;
  // Allowlist registration config
  allowlist_registration_enabled: boolean;
  allowlist_registration_limit: number | null;
  allowlist_registration_default_mints: number;
  allowlist_registration_deadline: string | null;
  allowlist_2_registration_enabled: boolean;
  allowlist_2_registration_limit: number | null;
  allowlist_2_registration_default_mints: number;
  allowlist_2_registration_deadline: string | null;
  // Registration requirements
  allowlist_registration_requirements: RegistrationRequirement[] | null;
  allowlist_2_registration_requirements: RegistrationRequirement[] | null;
  free_mint_enabled: boolean;
  public_phase_price_locked: boolean;
  total_revenue_drops: number;
  settled_revenue_drops: number;
  pending_settlement_drops: number;
  created_at: string;
  updated_at: string;
  launched_at: string | null;
  completed_at: string | null;
  // Referral program
  referral_enabled: boolean;
  referral_bps: number | null;
  // Composable fields
  collection_type?: string;
  base_image_url?: string | null;
  compose_enabled?: boolean;
  composition_fee_per_nft_drops?: number;
  total_composed_count?: number;
  total_burned_count?: number;
  compose_revenue_drops?: number;
}

export interface PriceLocking {
  allowlist_price_locked: boolean;
  allowlist_2_price_locked: boolean;
  public_price_locked: boolean;
  can_update_allowlist_price: boolean;
  can_update_allowlist_2_price: boolean;
  can_update_public_price: boolean;
}

export interface LaunchWithStats extends Launch {
  item_counts: ItemCounts;
  total_items: number;
  allowlist_count?: number;
  allowlist_2_count?: number;
  calculated_fee?: {
    feeDrops: number;
    feeXrp: number;
    baseFeeXrp: number;
    perNftFeeXrp: number;
  };
  price_locking?: PriceLocking;
  ipfs_progress?: {
    total: number;
    completed: number;
  } | null;
  // Project association
  project?: { id: string; slug: string; name: string; enabled_features?: string[] } | null;
  // Error tracking fields
  consecutive_errors?: number;
  last_error_at?: string | null;
  auto_paused_at?: string | null;
  auto_pause_reason?: string | null;
}

export interface LaunchListItem extends Launch {
  item_counts: ItemCounts;
  total_items: number;
}

export interface LaunchItem {
  id: string;
  launch_id: string;
  token_number: number;
  name: string | null;
  description: string | null;
  attributes: unknown;
  original_image_key: string | null;
  thumbnail_key: string | null;
  original_metadata: unknown;
  image_cid: string | null;
  metadata_cid: string | null;
  metadata_uri: string | null;
  status: 'pending' | 'reserved' | 'minting' | 'minted' | 'failed';
  reserved_for_address: string | null;
  reserved_at: string | null;
  reservation_expires_at: string | null;
  reservation_payment_tx: string | null;
  nft_token_id: string | null;
  mint_tx_hash: string | null;
  minted_to_address: string | null;
  minted_at: string | null;
  transfer_offer_id: string | null;
  transfer_tx_hash: string | null;
  transferred_at: string | null;
  error_message: string | null;
  retry_count: number;
  created_at: string;
  updated_at: string;
}

export interface AllowlistEntry {
  id: string;
  launch_id: string;
  wallet_address: string;
  mint_limit: number;
  minted_count: number;
  source?: 'manual' | 'csv' | 'registration';
  registration_metadata?: Record<string, any> | null;
  created_at: string;
  updated_at: string;
}

export type AllowlistFilterStatus = 'all' | 'has_minted' | 'hasnt_minted' | 'maxed_out';

export interface AllowlistStats {
  total_entries: number;
  total_remaining_mints: number;
  entries_with_mints_left: number;
}

export interface AllowlistImportPreview {
  newCount: number;
  updateCount: number;
  invalidCount: number;
  invalidEntries: Array<{ address: string; reason: string }>;
}

export interface CreateLaunchInput {
  name: string;
  slug: string;
  description?: string | null;
  taxon: number; // Required: XRPL NFT taxon (0-4294967295)
  website_url?: string | null;
  twitter_handle?: string | null;
  discord_url?: string | null;
  collection_type?: 'standard' | 'composable' | 'edition';
  project_id?: string | null;
}

// ==================== Edition Launches ====================

/**
 * Input for registering one edition (artwork) on an edition launch
 */
export interface RegisterEditionInput {
  editionNumber: number;
  name: string;
  description?: string | null;
  attributes?: { trait_type: string; value: string | number }[] | null;
  imageUrl: string;
  thumbnailUrl?: string | null;
  animationUrl?: string | null;
  maxSupply: number;
  /** Edition's own price in drops; null/omitted = launch phase pricing applies */
  priceDrops?: number | null;
  sortOrder?: number | null;
}

/**
 * Edition with availability counts (creator view)
 */
export interface LaunchEdition {
  id: string;
  launch_id: string;
  edition_number: number;
  name: string;
  description: string | null;
  attributes: { trait_type: string; value: string | number }[] | null;
  original_image_key: string | null;
  thumbnail_key: string | null;
  animation_key: string | null;
  image_cid: string | null;
  max_supply: number;
  price_drops: number | null;
  sort_order: number | null;
  minted_count: number;
  available_count: number;
  sold_out: boolean;
}

export interface UpdateLaunchInput {
  name?: string;
  description?: string | null;
  website_url?: string | null;
  twitter_handle?: string | null;
  discord_url?: string | null;
  mint_price_drops?: number | null; // Public phase price
  allowlist_mint_price_drops?: number | null; // Allowlist phase 1 price
  allowlist_2_mint_price_drops?: number | null; // Allowlist phase 2 price
  transfer_fee?: number;
  nft_flags?: number;
  limit_per_wallet?: number | null; // Public phase limit
  allowlist_enabled?: boolean;
  allowlist_2_enabled?: boolean;
  allowlist_start_at?: string | null;
  allowlist_2_start_at?: string | null;
  public_start_at?: string | null;
  end_at?: string | null;
  composition_fee_per_nft_drops?: number;
  // Allowlist registration config
  allowlist_registration_enabled?: boolean;
  allowlist_registration_limit?: number | null;
  allowlist_registration_default_mints?: number;
  allowlist_registration_deadline?: string | null;
  allowlist_2_registration_enabled?: boolean;
  allowlist_2_registration_limit?: number | null;
  allowlist_2_registration_default_mints?: number;
  allowlist_2_registration_deadline?: string | null;
  // Registration requirements
  allowlist_registration_requirements?: RegistrationRequirement[] | null;
  allowlist_2_registration_requirements?: RegistrationRequirement[] | null;
}

export interface AddAllowlistEntry {
  address: string;
  max_mint?: number;
  tier?: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  pages: number;
}

// ==================== API Functions ====================

/**
 * List user's launches
 */
export async function getLaunches(params: {
  page?: number;
  limit?: number;
  status?: string;
} = {}): Promise<{
  launches: LaunchListItem[];
  pagination: Pagination;
}> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.status) searchParams.set('status', params.status);

  const queryString = searchParams.toString();
  const endpoint = `/launches${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<ApiResponse<{
    launches: LaunchListItem[];
    pagination: Pagination;
  }>>(endpoint);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch launches');
  }

  return response.data;
}

/**
 * Get launch by ID
 */
export async function getLaunch(id: string): Promise<LaunchWithStats> {
  const response = await apiClient.get<ApiResponse<LaunchWithStats>>(
    `/launches/${id}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch launch');
  }

  return response.data;
}

/**
 * Create a new launch
 */
export async function createLaunch(
  data: CreateLaunchInput
): Promise<Launch> {
  const response = await apiClient.post<ApiResponse<Launch>>(
    '/launches',
    data
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create launch');
  }

  return response.data;
}

/**
 * Update launch settings
 */
export async function updateLaunch(
  id: string,
  data: UpdateLaunchInput
): Promise<Launch> {
  const response = await apiClient.put<ApiResponse<Launch>>(
    `/launches/${id}`,
    data
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to update launch');
  }

  return response.data;
}

/**
 * Delete a draft launch
 */
export async function deleteLaunch(id: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<{ message: string }>>(
    `/launches/${id}`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to delete launch');
  }
}

/**
 * Check if a slug is available
 */
export async function checkSlugAvailable(slug: string, excludeLaunchId?: string): Promise<boolean> {
  const body: { slug: string; excludeLaunchId?: string } = { slug };
  if (excludeLaunchId) {
    body.excludeLaunchId = excludeLaunchId;
  }
  const response = await apiClient.post<ApiResponse<{ available: boolean }>>(
    '/launches/check-slug',
    body
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to check slug');
  }

  return response.data.available;
}

/**
 * Get launch assets
 */
export async function getLaunchAssets(
  id: string,
  params: { page?: number; limit?: number } = {}
): Promise<{
  items: LaunchItem[];
  pagination: Pagination;
}> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());

  const queryString = searchParams.toString();
  const endpoint = `/launches/${id}/assets${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<ApiResponse<{
    items: LaunchItem[];
    pagination: Pagination;
  }>>(endpoint);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch assets');
  }

  return response.data;
}

/**
 * Get a single asset by token number
 */
export async function getLaunchAsset(
  id: string,
  tokenNumber: number
): Promise<LaunchItem> {
  const response = await apiClient.get<ApiResponse<LaunchItem>>(
    `/launches/${id}/assets/${tokenNumber}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch asset');
  }

  return response.data;
}

/**
 * Clear all assets
 */
export async function clearLaunchAssets(id: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<{ message: string }>>(
    `/launches/${id}/assets`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to clear assets');
  }
}

/**
 * Upload a single asset (image + metadata) for a launch
 */
export async function uploadLaunchAsset(
  launchId: string,
  tokenNumber: number,
  imageFile: File,
  metadata: { name: string; description?: string | null; attributes?: unknown; image?: string }
): Promise<LaunchItem> {
  const formData = new FormData();
  formData.append('file', imageFile);
  formData.append('metadata', JSON.stringify(metadata));

  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

  const response = await fetch(`${API_BASE_URL}/launches/${launchId}/assets/${tokenNumber}`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || `Upload failed with status ${response.status}`);
  }

  const data = await response.json();
  if (!data.success || !data.data) {
    throw new Error(data.error || 'Failed to upload asset');
  }

  return data.data;
}

/**
 * Get presigned S3 URLs for direct browser-to-S3 asset uploads
 */
export interface PresignedAssetUrl {
  tokenNumber: number;
  uploadUrl: string;
  cdnUrl: string;
  thumbnailUploadUrl?: string;
  thumbnailCdnUrl?: string;
  animationUploadUrl?: string;
  animationCdnUrl?: string;
}

export async function getPresignedAssetUrls(
  launchId: string,
  items: { tokenNumber: number; contentType: string; animationContentType?: string }[]
): Promise<{ urls: PresignedAssetUrl[] }> {
  const response = await apiClient.post<ApiResponse<{
    urls: PresignedAssetUrl[];
  }>>(`/launches/${launchId}/presign-assets`, { items });

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get presigned URLs');
  }

  return response.data;
}

/**
 * Register asset metadata in bulk after direct S3 uploads
 */
export async function registerLaunchAssets(
  launchId: string,
  items: { tokenNumber: number; name: string; description?: string | null; attributes?: unknown; imageUrl: string; thumbnailUrl?: string; animationUrl?: string; originalMetadata?: Record<string, unknown> }[]
): Promise<{ registered: number }> {
  const response = await apiClient.post<ApiResponse<{ registered: number }>>(
    `/launches/${launchId}/register-assets`,
    { items }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to register assets');
  }

  return response.data;
}

/**
 * Upload a file directly to S3 using a presigned URL
 */
export async function uploadToS3(
  presignedUrl: string,
  file: File | Blob,
  contentType: string
): Promise<void> {
  const response = await fetch(presignedUrl, {
    method: 'PUT',
    body: file,
    headers: {
      'Content-Type': contentType,
    },
  });

  if (!response.ok) {
    throw new Error(`S3 upload failed with status ${response.status}`);
  }
}

/**
 * Finalize asset uploads - updates total_supply based on uploaded items
 */
export async function finalizeLaunchAssets(
  launchId: string
): Promise<{ total_supply: number; item_count: number }> {
  const response = await apiClient.post<ApiResponse<{ total_supply: number; item_count: number }>>(
    `/launches/${launchId}/assets/finalize`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to finalize assets');
  }

  return response.data;
}

/**
 * Register editions for an edition (print-style) launch.
 * Replaces previously registered editions and expands copies into items.
 */
export async function registerEditions(
  launchId: string,
  editions: RegisterEditionInput[]
): Promise<{ editions: number; total_copies: number }> {
  const response = await apiClient.post<ApiResponse<{ editions: number; total_copies: number }>>(
    `/launches/${launchId}/register-editions`,
    { editions }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to register editions');
  }

  return response.data;
}

/**
 * List editions with availability (creator view)
 */
export async function getLaunchEditions(launchId: string): Promise<LaunchEdition[]> {
  const response = await apiClient.get<ApiResponse<{ editions: LaunchEdition[] }>>(
    `/launches/${launchId}/editions`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch editions');
  }

  return response.data.editions;
}

/**
 * Upload launch thumbnail
 */
export async function uploadLaunchThumbnail(
  id: string,
  file: File
): Promise<{ thumbnail_url: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

  const response = await fetch(`${API_BASE_URL}/launches/${id}/thumbnail`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to upload thumbnail');
  }

  const data = await response.json();
  if (!data.success || !data.data) {
    throw new Error(data.error || 'Failed to upload thumbnail');
  }

  return data.data;
}

/**
 * Upload launch banner
 */
export async function uploadLaunchBanner(
  id: string,
  file: File
): Promise<{ banner_url: string }> {
  const formData = new FormData();
  formData.append('file', file);

  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

  const response = await fetch(`${API_BASE_URL}/launches/${id}/banner`, {
    method: 'POST',
    headers,
    body: formData,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to upload banner');
  }

  const data = await response.json();
  if (!data.success || !data.data) {
    throw new Error(data.error || 'Failed to upload banner');
  }

  return data.data;
}

/**
 * Delete launch banner
 */
export async function deleteLaunchBanner(id: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<{ message: string }>>(
    `/launches/${id}/banner`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to remove banner');
  }
}

/**
 * Update banner vertical position
 */
export async function updateBannerPosition(
  id: string,
  positionY: number
): Promise<{ banner_position_y: number }> {
  const response = await apiClient.patch<ApiResponse<{ banner_position_y: number }>>(
    `/launches/${id}/banner-position`,
    { position_y: positionY }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to update banner position');
  }

  return response.data;
}

/**
 * Get allowlist entries with optional search, filter, and stats
 */
export async function getAllowlist(
  id: string,
  params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: AllowlistFilterStatus;
  } = {}
): Promise<{
  entries: AllowlistEntry[];
  pagination: Pagination;
  stats: AllowlistStats;
}> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.search) searchParams.set('search', params.search);
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);

  const queryString = searchParams.toString();
  const endpoint = `/launches/${id}/allowlist${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<ApiResponse<{
    entries: AllowlistEntry[];
    pagination: Pagination;
    stats: AllowlistStats;
  }>>(endpoint);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch allowlist');
  }

  return response.data;
}

/**
 * Add entries to allowlist
 */
export async function addToAllowlist(
  id: string,
  entries: AddAllowlistEntry[]
): Promise<{ added: number }> {
  const response = await apiClient.post<ApiResponse<{ added: number }>>(
    `/launches/${id}/allowlist`,
    { entries }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to add to allowlist');
  }

  return response.data;
}

/**
 * Clear allowlist
 */
export async function clearAllowlist(id: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<{ message: string }>>(
    `/launches/${id}/allowlist`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to clear allowlist');
  }
}

/**
 * Remove a single address from allowlist
 */
export async function removeFromAllowlist(id: string, address: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<{ message: string }>>(
    `/launches/${id}/allowlist/${encodeURIComponent(address)}`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to remove address from allowlist');
  }
}

/**
 * Update a single allowlist entry (inline edit)
 */
export async function updateAllowlistEntry(
  id: string,
  address: string,
  mintLimit: number
): Promise<AllowlistEntry> {
  const response = await apiClient.patch<ApiResponse<AllowlistEntry>>(
    `/launches/${id}/allowlist/${encodeURIComponent(address)}`,
    { mint_limit: mintLimit }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to update allowlist entry');
  }

  return response.data;
}

/**
 * Preview CSV import without persisting
 */
export async function previewAllowlistImport(
  id: string,
  entries: Array<{ wallet_address: string; mint_limit?: number }>
): Promise<AllowlistImportPreview> {
  const response = await apiClient.post<ApiResponse<AllowlistImportPreview>>(
    `/launches/${id}/allowlist/preview`,
    { entries }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to preview allowlist import');
  }

  return response.data;
}

/**
 * Bulk delete addresses from allowlist
 */
export async function bulkDeleteFromAllowlist(
  id: string,
  addresses: string[]
): Promise<{ deleted: number }> {
  const response = await apiClient.post<ApiResponse<{ deleted: number }>>(
    `/launches/${id}/allowlist/bulk-delete`,
    { addresses }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to bulk delete from allowlist');
  }

  return response.data;
}

/**
 * Bulk update mint limit for addresses
 */
export async function bulkUpdateAllowlistLimit(
  id: string,
  addresses: string[],
  mintLimit: number
): Promise<{ updated: number }> {
  const response = await apiClient.post<ApiResponse<{ updated: number }>>(
    `/launches/${id}/allowlist/bulk-update-limit`,
    { addresses, mint_limit: mintLimit }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to bulk update allowlist limits');
  }

  return response.data;
}

// ==================== Allowlist Phase 2 API Functions ====================

/**
 * Get allowlist phase 2 entries with optional search, filter, and stats
 */
export async function getAllowlist2(
  id: string,
  params: {
    page?: number;
    limit?: number;
    search?: string;
    status?: AllowlistFilterStatus;
  } = {}
): Promise<{
  entries: AllowlistEntry[];
  pagination: Pagination;
  stats: AllowlistStats;
}> {
  const searchParams = new URLSearchParams();

  if (params.page) searchParams.set('page', params.page.toString());
  if (params.limit) searchParams.set('limit', params.limit.toString());
  if (params.search) searchParams.set('search', params.search);
  if (params.status && params.status !== 'all') searchParams.set('status', params.status);

  const queryString = searchParams.toString();
  const endpoint = `/launches/${id}/allowlist-2${queryString ? `?${queryString}` : ''}`;

  const response = await apiClient.get<ApiResponse<{
    entries: AllowlistEntry[];
    pagination: Pagination;
    stats: AllowlistStats;
  }>>(endpoint);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to fetch allowlist phase 2');
  }

  return response.data;
}

/**
 * Add entries to allowlist phase 2
 */
export async function addToAllowlist2(
  id: string,
  entries: AddAllowlistEntry[]
): Promise<{ added: number }> {
  const response = await apiClient.post<ApiResponse<{ added: number }>>(
    `/launches/${id}/allowlist-2`,
    { entries }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to add to allowlist phase 2');
  }

  return response.data;
}

/**
 * Clear allowlist phase 2
 */
export async function clearAllowlist2(id: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<{ message: string }>>(
    `/launches/${id}/allowlist-2`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to clear allowlist phase 2');
  }
}

/**
 * Remove a single address from allowlist phase 2
 */
export async function removeFromAllowlist2(id: string, address: string): Promise<void> {
  const response = await apiClient.delete<ApiResponse<{ message: string }>>(
    `/launches/${id}/allowlist-2/${encodeURIComponent(address)}`
  );

  if (!response.success) {
    throw new Error(response.error || 'Failed to remove address from allowlist phase 2');
  }
}

/**
 * Update a single allowlist phase 2 entry (inline edit)
 */
export async function updateAllowlist2Entry(
  id: string,
  address: string,
  mintLimit: number
): Promise<AllowlistEntry> {
  const response = await apiClient.patch<ApiResponse<AllowlistEntry>>(
    `/launches/${id}/allowlist-2/${encodeURIComponent(address)}`,
    { mint_limit: mintLimit }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to update allowlist phase 2 entry');
  }

  return response.data;
}

/**
 * Preview CSV import without persisting for allowlist phase 2
 */
export async function previewAllowlist2Import(
  id: string,
  entries: Array<{ wallet_address: string; mint_limit?: number }>
): Promise<AllowlistImportPreview> {
  const response = await apiClient.post<ApiResponse<AllowlistImportPreview>>(
    `/launches/${id}/allowlist-2/preview`,
    { entries }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to preview allowlist phase 2 import');
  }

  return response.data;
}

/**
 * Bulk delete addresses from allowlist phase 2
 */
export async function bulkDeleteFromAllowlist2(
  id: string,
  addresses: string[]
): Promise<{ deleted: number }> {
  const response = await apiClient.post<ApiResponse<{ deleted: number }>>(
    `/launches/${id}/allowlist-2/bulk-delete`,
    { addresses }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to bulk delete from allowlist phase 2');
  }

  return response.data;
}

/**
 * Bulk update mint limit for addresses in allowlist phase 2
 */
export async function bulkUpdateAllowlist2Limit(
  id: string,
  addresses: string[],
  mintLimit: number
): Promise<{ updated: number }> {
  const response = await apiClient.post<ApiResponse<{ updated: number }>>(
    `/launches/${id}/allowlist-2/bulk-update-limit`,
    { addresses, mint_limit: mintLimit }
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to bulk update allowlist phase 2 limits');
  }

  return response.data;
}

/**
 * Submit launch for approval (legacy - use createFeePayment for payment flow)
 */
export async function submitForApproval(id: string): Promise<{
  total_supply: number;
  fee_amount_drops: number;
  fee_amount_xrp: number;
}> {
  const response = await apiClient.post<ApiResponse<{
    message: string;
    total_supply: number;
    fee_amount_drops: number;
    fee_amount_xrp: number;
  }>>(`/launches/${id}/submit`);

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to submit for approval');
  }

  return {
    total_supply: response.data.total_supply,
    fee_amount_drops: response.data.fee_amount_drops,
    fee_amount_xrp: response.data.fee_amount_xrp,
  };
}

/**
 * Fee payment payload response
 */
export interface FeePaymentPayload {
  /** Xaman-only refs. Absent for client-signing wallets — use `signingIntent`. */
  payload?: XamanPayloadResponse;
  signingIntent?: SigningIntentResponse;
  fee: {
    totalItems: number;
    feeDrops: number;
    feeXrp: number;
    baseFeeXrp: number;
    perNftFeeXrp: number;
  };
}

/**
 * Fee payment status response
 */
export interface FeePaymentStatus {
  status: 'pending' | 'paid' | 'rejected' | 'expired' | 'unknown';
  message?: string;
  txHash?: string;
  launch_status?: LaunchStatus;
}

/**
 * Create a fee payment request (initiates Xaman payment flow)
 */
export async function createFeePayment(id: string): Promise<FeePaymentPayload> {
  const response = await apiClient.post<ApiResponse<FeePaymentPayload>>(
    `/launches/${id}/fee-payment`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create fee payment');
  }

  return response.data;
}

/**
 * Check fee payment status
 */
export async function checkFeePaymentStatus(
  id: string,
  payloadUuid: string
): Promise<FeePaymentStatus> {
  const response = await apiClient.get<ApiResponse<FeePaymentStatus>>(
    `/launches/${id}/fee-payment/${payloadUuid}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to check payment status');
  }

  return response.data;
}

/**
 * Transition from allowlist phase to public phase (irreversible)
 * @deprecated Use advancePhase instead for more flexibility
 */
export async function transitionToPublicPhase(id: string): Promise<Launch> {
  const response = await apiClient.post<ApiResponse<Launch>>(
    `/launches/${id}/transition-to-public`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to transition to public phase');
  }

  return response.data;
}

/**
 * Response from advancing to next phase
 */
export interface AdvancePhaseResponse {
  launch: Launch;
  previousPhase: string;
  newPhase: string;
}

/**
 * Advance to the next mint phase (irreversible)
 *
 * Phase transitions:
 * - allowlist → allowlist_2 (if enabled) or public
 * - allowlist_2 → public
 *
 * Also sets the appropriate start time (allowlist_2_start_at or public_start_at) to NOW.
 */
export async function advancePhase(id: string): Promise<AdvancePhaseResponse> {
  const response = await apiClient.post<ApiResponse<AdvancePhaseResponse>>(
    `/launches/${id}/advance-phase`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to advance phase');
  }

  return response.data;
}

// ==================== Authorization API Functions ====================

/**
 * Authorization payload response
 */
export interface AuthorizationPayload {
  /** Xaman-only refs. Absent for client-signing wallets — use `signingIntent`. */
  payload?: XamanPayloadResponse;
  signingIntent?: SigningIntentResponse;
  minterAddress: string;
}

/**
 * Authorization status response
 */
export interface AuthorizationStatus {
  status: 'pending' | 'authorized' | 'rejected' | 'expired' | 'failed' | 'verification_failed' | 'unknown';
  message?: string;
  txHash?: string;
  launch_status?: LaunchStatus;
}

/**
 * Authorization check response (direct XRPL check)
 */
export interface AuthorizationCheckResult {
  authorized: boolean;
  currentMinter?: string | null;
  expectedMinter?: string;
  launch_status?: LaunchStatus;
  txHash?: string;
  message?: string;
  error?: string;
}

/**
 * Create an authorization request (initiates Xaman AccountSet flow)
 */
export async function createAuthorizationRequest(id: string): Promise<AuthorizationPayload> {
  const response = await apiClient.post<ApiResponse<AuthorizationPayload>>(
    `/launches/${id}/authorize`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to create authorization request');
  }

  return response.data;
}

/**
 * Check authorization payload status
 */
export async function checkAuthorizationStatus(
  id: string,
  payloadUuid: string
): Promise<AuthorizationStatus> {
  const response = await apiClient.get<ApiResponse<AuthorizationStatus>>(
    `/launches/${id}/authorize/${payloadUuid}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to check authorization status');
  }

  return response.data;
}

/**
 * Check if authorization is complete on XRPL (direct check)
 */
export async function checkAuthorizationOnXRPL(id: string): Promise<AuthorizationCheckResult> {
  const response = await apiClient.get<ApiResponse<AuthorizationCheckResult>>(
    `/launches/${id}/authorization-status`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to check authorization on XRPL');
  }

  return response.data;
}

// ==================== Utility Functions ====================

/**
 * Convert drops to XRP
 * Uses integer math to avoid floating-point precision issues
 */
export function dropsToXrp(drops: number | null | undefined): number | null {
  if (drops === null || drops === undefined) return null;
  // Use integer division + remainder to avoid floating-point issues
  const wholeXrp = Math.floor(drops / 1_000_000);
  const remainder = drops % 1_000_000;
  return wholeXrp + remainder / 1_000_000;
}

/**
 * Convert XRP to drops
 */
export function xrpToDrops(xrp: number): number {
  return Math.round(xrp * 1_000_000);
}

/**
 * Convert drops to XRP rounded to nearest tenth (0.1 XRP precision).
 * Use for mint price inputs where tenths precision is the maximum allowed.
 */
export function dropsToXrpTenths(drops: number | null | undefined): number {
  if (drops === null || drops === undefined) return 0;
  return Math.round(drops / 100_000) / 10;
}

/**
 * Convert XRP to drops, rounding to nearest tenth of XRP (100,000 drops).
 * Use for mint price inputs where tenths precision is the maximum allowed.
 */
export function xrpTenthsToDrops(xrp: number): number {
  return Math.round(xrp * 10) * 100_000;
}

/** @deprecated Use dropsToXrpTenths instead */
export function dropsToWholeXrp(drops: number | null | undefined): number {
  return dropsToXrpTenths(drops);
}

/** @deprecated Use xrpTenthsToDrops instead */
export function wholeXrpToDrops(xrp: number): number {
  return xrpTenthsToDrops(xrp);
}

/**
 * Format mint price for display
 */
export function formatMintPrice(drops: number | null | undefined): string {
  if (drops === null || drops === undefined) return 'Not set';
  if (drops === 0) return 'Free';
  const xrp = dropsToXrpTenths(drops);
  const formatted = xrp % 1 === 0 ? xrp.toLocaleString() : xrp.toLocaleString(undefined, { minimumFractionDigits: 1, maximumFractionDigits: 1 });
  return `${formatted} XRP`;
}

/**
 * Get status display info
 */
export function getStatusDisplay(status: LaunchStatus): {
  label: string;
  color: 'gray' | 'yellow' | 'blue' | 'green' | 'red';
} {
  switch (status) {
    case 'draft':
      return { label: 'Draft', color: 'gray' };
    case 'pending_approval':
      return { label: 'Pending Approval', color: 'yellow' };
    case 'pending_authorization':
      return { label: 'Pending Authorization', color: 'yellow' };
    case 'pending_payment':
      return { label: 'Pending Payment', color: 'yellow' };
    case 'processing':
      return { label: 'Processing', color: 'blue' };
    case 'ready':
      return { label: 'Ready', color: 'green' };
    case 'active':
      return { label: 'Active', color: 'green' };
    case 'paused':
      return { label: 'Paused', color: 'yellow' };
    case 'completed':
      return { label: 'Completed', color: 'gray' };
    case 'cancelled':
      return { label: 'Cancelled', color: 'red' };
    default:
      return { label: status, color: 'gray' };
  }
}

/**
 * Generate slug from name
 */
export function generateSlug(name: string): string {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .slice(0, 50);
}

// ==================== Lifecycle Control API Functions ====================

/**
 * Activate a launch (go live now)
 * Only works for launches in 'ready' status
 */
export async function activateLaunch(id: string): Promise<Launch> {
  const response = await apiClient.post<ApiResponse<Launch>>(
    `/launches/${id}/activate`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to activate launch');
  }

  return response.data;
}

/**
 * Pause an active launch
 * Only works for launches in 'active' status
 */
export async function pauseLaunch(id: string): Promise<Launch> {
  const response = await apiClient.post<ApiResponse<Launch>>(
    `/launches/${id}/pause`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to pause launch');
  }

  return response.data;
}

/**
 * Resume a paused launch
 * Only works for launches in 'paused' status
 */
export async function resumeLaunch(id: string): Promise<Launch> {
  const response = await apiClient.post<ApiResponse<Launch>>(
    `/launches/${id}/resume`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to resume launch');
  }

  return response.data;
}

// ==================== Revenue & Settlement API Functions ====================

export interface LaunchSettlement {
  id: string;
  launch_id: string;
  creator_address: string;
  amount_drops: number;
  mint_count: number;
  tx_hash: string;
  ledger_index: number | null;
  status: 'completed' | 'failed';
  error_message: string | null;
  created_at: string;
}

export interface SettlementStats {
  total_settlements: number;
  total_amount_drops: number;
  total_amount_xrp: number;
  total_mints_settled: number;
}

export interface SettlementsResponse {
  settlements: LaunchSettlement[];
  stats: SettlementStats;
  pagination: {
    page: number;
    limit: number;
    total: number;
    total_pages: number;
  };
}

export interface RevenueStats {
  total_revenue_drops: number;
  total_revenue_xrp: number;
  settled_revenue_drops: number;
  settled_revenue_xrp: number;
  pending_settlement_drops: number;
  pending_settlement_xrp: number;
  total_mints: number;
  completed_mints: number;
  failed_mints: number;
  unique_minters: number;
  total_settlements: number;
  total_mints_settled: number;
}

/**
 * Get revenue statistics for a launch
 */
export async function getLaunchRevenue(id: string): Promise<RevenueStats> {
  const response = await apiClient.get<ApiResponse<RevenueStats>>(
    `/launches/${id}/revenue`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get revenue stats');
  }

  return response.data;
}

/**
 * Get settlement history for a launch
 */
export async function getLaunchSettlements(
  id: string,
  page = 1,
  limit = 20
): Promise<SettlementsResponse> {
  const response = await apiClient.get<ApiResponse<SettlementsResponse>>(
    `/launches/${id}/settlements?page=${page}&limit=${limit}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get settlements');
  }

  return response.data;
}

/**
 * Export settlement history as CSV
 * Triggers a file download
 */
export async function exportLaunchSettlements(id: string): Promise<void> {
  const token = localStorage.getItem('xrpl_marketplace_token');
  const headers: Record<string, string> = {};
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }

  const API_BASE_URL = import.meta.env.VITE_PUBLIC_API_BASE_URL || 'http://localhost:3703/api/v1';

  const response = await fetch(`${API_BASE_URL}/launches/${id}/settlements/export`, {
    method: 'GET',
    headers,
    credentials: 'include',
  });

  if (!response.ok) {
    const errorData = await response.json().catch(() => ({}));
    throw new Error(errorData.error || 'Failed to export settlements');
  }

  // Get filename from Content-Disposition header or use default
  const contentDisposition = response.headers.get('Content-Disposition');
  let filename = 'settlements.csv';
  if (contentDisposition) {
    const filenameMatch = contentDisposition.match(/filename="([^"]+)"/);
    if (filenameMatch) {
      filename = filenameMatch[1];
    }
  }

  // Create blob and trigger download
  const blob = await response.blob();
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  window.URL.revokeObjectURL(url);
  document.body.removeChild(a);
}

// ==================== Royalty Types ====================

/**
 * A secondary sale with royalty information
 */
export interface RoyaltySale {
  txHash: string;
  salePriceXrp: number;
  royaltyXrp: number;
  buyerAddress: string;
  sellerAddress?: string;
  soldAt: string;
  nftTokenId: string;
  nftName: string | null;
  nftImageUrl?: string | null;
}

/**
 * Royalty statistics for a launch
 */
export interface LaunchRoyaltyStats {
  totalSecondarySales: number;
  totalSecondaryVolumeXrp: number;
  totalRoyaltiesXrp: number;
  transferFeePercent: number;
  lastSaleAt: string | null;
  lastCalculatedAt: string;
  recentSales: RoyaltySale[];
}

/**
 * Paginated royalty history response
 */
export interface RoyaltyHistoryResponse {
  sales: RoyaltySale[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
  transferFeePercent: number;
}

// ==================== Royalty API Functions ====================

/**
 * Get royalty statistics for a launch
 */
export async function getLaunchRoyalties(id: string): Promise<LaunchRoyaltyStats> {
  const response = await apiClient.get<ApiResponse<LaunchRoyaltyStats>>(
    `/launches/${id}/royalties`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get royalty stats');
  }

  return response.data;
}

/**
 * Get paginated royalty history for a launch
 */
export async function getLaunchRoyaltyHistory(
  id: string,
  page: number = 1,
  limit: number = 20
): Promise<RoyaltyHistoryResponse> {
  const response = await apiClient.get<ApiResponse<RoyaltyHistoryResponse>>(
    `/launches/${id}/royalties/history?page=${page}&limit=${limit}`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get royalty history');
  }

  return response.data;
}

// ==================== Validation & Preview Types (Phase 10 Part 3) ====================

/**
 * A single validation check result
 */
export interface ValidationCheck {
  id: string;
  name: string;
  status: 'pass' | 'warn' | 'fail';
  message: string;
}

/**
 * Validation result summary
 */
export interface ValidationSummary {
  total: number;
  passed: number;
  warnings: number;
  failures: number;
}

/**
 * Full validation response
 */
export interface LaunchValidationResult {
  valid: boolean;
  readyForSubmission: boolean;
  hasWarnings: boolean;
  checks: ValidationCheck[];
  summary: ValidationSummary;
}

/**
 * Preview eligibility info (same as drops eligibility)
 */
export interface PreviewEligibility {
  eligible: boolean;
  reason?: string;
  mintPhase: MintPhase | null;
  priceDrops: number;
  maxAllowed: number;
}

/**
 * Preview sample item (includes staging image for non-IPFS items)
 */
export interface PreviewSampleItem {
  id: string;
  token_number: number;
  name: string | null;
  image_cid: string | null;
  status: string;
  original_image_key?: string | null;
}

/**
 * User's allowlist status in the preview
 */
export interface PreviewAllowlistStatus {
  isOnAllowlist: boolean;
  isOnAllowlist2: boolean;
  mintLimit?: number;
  mintedCount?: number;
  mintLimit2?: number;
  mintedCount2?: number;
}

/**
 * Preview drop data (same structure as public drop with some extras)
 */
export interface PreviewDrop {
  id: string;
  name: string;
  slug: string;
  description: string;
  thumbnail_url: string | null;
  banner_url: string | null;
  banner_position_y: number;
  creator_address: string;
  mint_phase: MintPhase | null;
  status: LaunchStatus;
  total_supply: number;
  minted_count: number;
  available_count: number;
  mint_price_drops: number | null;
  allowlist_mint_price_drops: number | null;
  allowlist_2_mint_price_drops: number | null;
  transfer_fee: number;
  allowlist_enabled: boolean;
  allowlist_2_enabled: boolean;
  allowlist_start_at: string | null;
  allowlist_2_start_at: string | null;
  public_start_at: string | null;
  end_at: string | null;
  website_url: string | null;
  twitter_handle: string | null;
  discord_url: string | null;
  userAllowlistStatus: PreviewAllowlistStatus | null;
}

/**
 * Full preview response
 */
export interface LaunchPreviewResult {
  drop: PreviewDrop;
  sampleItems: PreviewSampleItem[];
  eligibility: PreviewEligibility | null;
  isPreview: boolean;
  previewMessage: string;
}

// ==================== Validation & Preview API Functions ====================

/**
 * Run comprehensive validation checks on a launch
 *
 * Returns detailed validation results for the pre-launch checklist.
 * Checks include: collection info, images, assets, pricing, schedule, etc.
 */
export async function validateLaunch(id: string): Promise<LaunchValidationResult> {
  const response = await apiClient.post<ApiResponse<LaunchValidationResult>>(
    `/launches/${id}/validate`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to validate launch');
  }

  return response.data;
}

/**
 * Get preview data for a launch (creator only)
 *
 * Returns the same data structure as the public /drops/:slug endpoint,
 * but works for launches that are not yet public.
 * Used by the preview page to show creators what buyers will see.
 */
export async function getLaunchPreview(id: string): Promise<LaunchPreviewResult> {
  const response = await apiClient.get<ApiResponse<LaunchPreviewResult>>(
    `/launches/${id}/preview`
  );

  if (!response.success || !response.data) {
    throw new Error(response.error || 'Failed to get launch preview');
  }

  return response.data;
}

// ==================== Referral Configuration ====================

export interface ReferralConfig {
  referral_enabled: boolean;
  referral_bps: number | null;
  referral_code?: string;
  user_stats?: {
    referred_mints: number;
    total_earned_drops: number;
  };
  launch_stats?: {
    referred_mints: number;
    direct_mints: number;
    unique_referrers: number;
    total_referral_fee_drops: number;
  };
  top_referrers?: Array<{
    username: string | null;
    xrpl_address: string;
    referred_mints: number;
    total_earned_drops: number;
  }>;
}

/**
 * Get referral config for a launch
 */
export async function getLaunchReferralConfig(id: string): Promise<ReferralConfig> {
  const response = await apiClient.get<ReferralConfig & { success: boolean }>(
    `/launches/${id}/referrals`
  );

  if (!response.success) {
    throw new Error('Failed to get referral config');
  }

  return response;
}

/**
 * Update referral config for a launch
 */
export async function updateLaunchReferralConfig(
  id: string,
  data: { referral_enabled: boolean; referral_bps?: number | null }
): Promise<ReferralConfig> {
  const response = await apiClient.patch<ReferralConfig & { success: boolean }>(
    `/launches/${id}/referrals`,
    data
  );

  if (!response.success) {
    throw new Error('Failed to update referral config');
  }

  return response;
}

